<!-- Search Input -->
@if (isSearchRequired) {
  <div class="table-container">
    <mat-form-field class="small" appearance="outline">
      <mat-label>Search</mat-label>
      <input matInput type="text" [(ngModel)]="searchText" (ngModelChange)="applySearch()" />
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>
  </div>
}

<!-- Skeleton Loader -->
@if (data.length === 0) {
  <div class="skeleton-table">
    <div class="skeleton-header">
      @for (col of getColumns(); track col) {
        <div class="skeleton-cell"></div>
      }
    </div>
    @for (i of [1, 2, 3, 4]; track i) {
      <div class="skeleton-row">
        @for (col of getColumns(); track col) {
          <div class="skeleton-cell"></div>
        }
      </div>
    }
  </div>
}

<!-- Scrollable Table Wrapper -->
<div class="table-scroll-wrapper">
  @if (data.length > 0) {
    <mat-table
      [dataSource]="dataSource"
      matSort
      class="table mat-elevation-z2 rounded-4">
      <!-- Dynamic Columns -->
      @for (col of getColumns(); track col) {
        <ng-container [matColumnDef]="col">
          <mat-header-cell *matHeaderCellDef mat-sort-header class="t-header-cell">
            {{ col | titlecase }}
          </mat-header-cell>
          <mat-cell *matCellDef="let row" class="t-cell">
            @if (row.isEditing && row === editingRow) {
              <mat-form-field appearance="outline" class="w-100 m-0">
                <input matInput
                  [formControl]="getFormControl(col)"
                  [placeholder]="col | titlecase"
                  [ngClass]="{ 'invalid-cell': getInvalidCellStatus(row, col) }"
                  [matTooltip]="getValidationMessage(row, col)"
                  [matTooltipDisabled]="!getValidationMessage(row, col)"
                  matTooltipClass="tooltip-red"
                  matTooltipPosition="above"
                  showDelay="300"
                  />
                </mat-form-field>
                @if (getFormControl(col)?.invalid) {
                  <div class="inline-error">
                    {{ getValidationMessage(row, col) }}
                  </div>
                }
              } @else {
                <div class="cell-wrapper">
                  <span
                    [ngClass]="{ 'invalid-cell': getInvalidCellStatus(row, col) }"
                    [matTooltip]="getValidationMessage(row, col)"
                    [matTooltipDisabled]="!getValidationMessage(row, col)"
                    matTooltipClass="tooltip-red"
                    matTooltipPosition="above"
                    showDelay="300"
                    [ngStyle]="formatNegativeAmount(col, row[col]).style">
                    {{ formatNegativeAmount(col, row[col]).text }}
                  </span>
                  @if (getInvalidCellStatus(row, col)) {
                    <div class="inline-error">
                      {{ getValidationMessage(row, col) }}
                    </div>
                  }
                </div>
              }
            </mat-cell>
          </ng-container>
        }
        <!-- Actions Column -->
        @if (actions) {
          <ng-container matColumnDef="actions">
            <mat-header-cell *matHeaderCellDef class="t-header-cell text-center">Actions</mat-header-cell>
            <mat-cell *matCellDef="let row" class="t-cell text-center">
              <div class="d-flex justify-content-center gap-2">
                @if (!row.isEditing) {
                  @if (allowedActions.includes('editMain')) {
                    <mat-icon
                      matTooltip="Edit Main"
                      class="action-icon"
                      [class.disabled-icon]="!canEdit(row)"
                      role="button"
                      tabindex="0"
                      (click)="!editingRow ? onAction('showtables', row) : null"
                      (keydown.enter)="!editingRow && canEdit(row) ? onAction('showtables', row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow && canEdit(row) ? onAction('showtables', row) : null">
                      edit
                    </mat-icon>
                  }
                  @if (this.isMaster && allowedActions.includes('edit')) {
                    <mat-icon
                      matTooltip="Edit"
                      class="action-icon"
                      [class.disabled-icon]="!canEditMaster(row)"
                      role="button"
                      tabindex="0"
                      (click)="!editingRow ? startEdit(row) : null"
                      (keydown.enter)="!editingRow && canEditMaster(row) ? startEdit(row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow && canEditMaster(row) ? startEdit(row) : null">
                      edit
                    </mat-icon>
                  }
                  @if (!this.isMaster && allowedActions.includes('edit')) {
                    <mat-icon
                      matTooltip="Edit"
                      class="action-icon"
                      [class.disabled-icon]="!canEdit(row)"
                      role="button"
                      tabindex="0"
                      (click)="!editingRow ? startEdit(row) : null"
                      (keydown.enter)="!editingRow && canEdit(row) ? startEdit(row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow && canEdit(row) ? startEdit(row) : null">
                      edit
                    </mat-icon>
                  }
                  @if (this.isMaster && allowedActions.includes('delete')) {
                    <mat-icon
                      matTooltip="Delete"
                      class="action-icon"
                      [class.disabled-icon]="editingRow"
                      [class.disabled-icon]="!canDeleteMaster(row)"
                      role="button"
                      tabindex="0"
                      (click)="onAction('delete', row)"
                      (keydown.enter)="!editingRow && canDeleteMaster(row) ? onAction('delete', row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow && canDeleteMaster(row) ? onAction('delete', row) : null">delete</mat-icon>
                  }
                  @if (!this.isMaster && allowedActions.includes('delete')) {
                    <mat-icon
                      matTooltip="Delete"
                      class="action-icon"
                      [class.disabled-icon]="editingRow"
                      [class.disabled-icon]="!canDeleteInvoice(row)"
                      role="button"
                      tabindex="0"
                      (click)="onAction('delete', row)"
                      (keydown.enter)="!editingRow && canDeleteInvoice(row) ? onAction('delete', row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow && canDeleteInvoice(row) ? onAction('delete', row) : null">delete</mat-icon>
                  }
                  @if (allowedActions.includes('view')) {
                    <mat-icon
                      matTooltip="View"
                      class="action-icon"
                      [class.disabled-icon]="editingRow"
                      role="button"
                      tabindex="0"
                      (click)="onAction('view', row)"
                      (keydown.enter)="!editingRow ? onAction('view', row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow ? onAction('view', row) : null">visibility</mat-icon>
                  }
                  @if (allowedActions.includes('download')) {
                    <mat-icon
                      matTooltip="Download"
                      class="action-icon"
                      [class.disabled-icon]="editingRow"
                      role="button"
                      tabindex="0"
                      (click)="onAction('download', row)"
                      (keydown.enter)="!editingRow ? onAction('download', row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow ? onAction('download', row) : null">download</mat-icon>
                  }
                  @if (allowedActions.includes('digitalCopy')) {
                    <mat-icon
                      matTooltip="Digital Copy PDF"
                      class=" action-icon"
                      [class.disabled-icon]="editingRow"
                      role="button"
                      tabindex="0"
                      (click)="onAction('digitalCopy', row)"
                      (keydown.enter)="!editingRow ? onAction('digitalCopy', row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow ? onAction('digitalCopy', row) : null">picture_as_pdf</mat-icon>
                  }
                  @if (allowedActions.includes('stamp')) {
                    <mat-icon
                      matTooltip="Stamp"
                      class="action-icon"
                      [class.disabled-icon]="editingRow"
                      role="button"
                      tabindex="0"
                      (click)="onAction('stamp', row)"
                      (keydown.enter)="!editingRow ? onAction('stamp', row) : null"
                      (keydown.space)="$event.preventDefault(); !editingRow ? onAction('stamp', row) : null">approval</mat-icon>
                  }
                }
                @if (row.isEditing) {
                  <mat-icon matTooltip="Save" class="action-icon text-accent" role="button" tabindex="0"
                    (click)="saveEdit(row)" (keydown.enter)="saveEdit(row)"
                    (keydown.space)="$event.preventDefault(); saveEdit(row)">check</mat-icon>
                  <mat-icon matTooltip="Cancel" class="action-icon text-warn" role="button" tabindex="0"
                    (click)="cancelEdit(row)" (keydown.enter)="cancelEdit(row)"
                    (keydown.space)="$event.preventDefault(); cancelEdit(row)">close</mat-icon>
                }
              </div>
            </mat-cell>
          </ng-container>
        }
        <!-- Header & Row -->
        <mat-header-row *matHeaderRowDef="getColumns().concat(actions ? ['actions'] : [])"
        class="t-header-row"></mat-header-row>
        <mat-row *matRowDef="let row; columns: getColumns().concat(actions ? ['actions'] : [])"
          class="t-row"
              [ngClass]="{
                'editing-row': row.isEditing && row === editingRow,
                'blurred-row': editingRow && row !== editingRow,
                'edited-row': !row.isEditing && row._original
              }">
        </mat-row>
        <!-- No Data Row -->
        <ng-container *matNoDataRow>
          <mat-row>
            <mat-cell [attr.colspan]="getColumns().length + (actions ? 1 : 0)" class="nodata text-center">
              No Data Available
            </mat-cell>
          </mat-row>
        </ng-container>
      </mat-table>
    }
  </div>

  <!-- Paginator -->
  @if (data.length > 0 && isSearchRequired) {
    <mat-paginator
      class="t-pagination"
      [pageSize]="10"
      [pageSizeOptions]="[5, 10, 20]"
      [showFirstLastButtons]="true">
    </mat-paginator>
  }
